import { Box, CircularProgress, Menu, MenuItem, Typography } from '@mui/material';
import { type MouseEvent, useCallback, useState } from 'react';

import { taskPath } from '~/components/tasks/detail/TaskDetailDialog';
import type { ActionItem } from '~/data/schemas/actionItem';
import { useDetailTableHeight } from '~/hooks/useDetailTableHeight';
import useLAISnackbar from '~/hooks/useLAISnackbar';
import { buildUrlForContract, buildUrlForElevator, buildUrlForInvoice } from '~/utils/urlBuilder';

import { useHostedInWorkQueueDialog } from './dialogHost';
import type { WorkQueueDetailTarget } from './WorkQueueDetailDialog';
import { isWorkQueueDialogTarget, WorkQueueDetailDialog } from './WorkQueueDetailDialog';
import { WorkQueueTable } from './WorkQueueTable';

const PAGE_URL: Record<WorkQueueDetailTarget['type'], (id: string) => string> = {
  invoice: (id) => buildUrlForInvoice(id),
  contract: (id) => buildUrlForContract(id),
  compliance: (id) => buildUrlForElevator(id),
  task: (id) => taskPath(id),
};

const SNOOZE_OPTIONS: { label: string; days: number }[] = [
  { label: '1 day', days: 1 },
  { label: '3 days', days: 3 },
  { label: '1 week', days: 7 },
  { label: '1 month', days: 30 },
];

type Props = {
  items: ActionItem[];
  isLoading: boolean;
  isValidating: boolean;
  snooze: (id: string, days: number) => Promise<void>;
  refresh: () => void;
  /** What an empty queue means here — a property and an elevator say it differently. */
  emptyMessage: string;
};

/**
 * The work queue for one record, as the first tab of its detail page.
 *
 * No AI-pipeline dialogs, unlike the full page: those items carry no property until the
 * document is matched to one, so a property-scoped read never returns them.
 */
export function NeedsAttentionPanel({
  items,
  isLoading,
  isValidating,
  snooze,
  refresh,
  emptyMessage,
}: Props) {
  const [snoozeAnchor, setSnoozeAnchor] = useState<{
    el: HTMLElement;
    item: ActionItem;
  } | null>(null);
  const [detailTarget, setDetailTarget] = useState<WorkQueueDetailTarget | null>(null);
  const hostedInDialog = useHostedInWorkQueueDialog();
  const tableHeight = useDetailTableHeight();
  const { showSnackbar } = useLAISnackbar();

  const closeSnoozeMenu = () => setSnoozeAnchor(null);
  const openSnoozeMenu = useCallback((item: ActionItem, event: MouseEvent<HTMLElement>) => {
    setSnoozeAnchor({ el: event.currentTarget, item });
  }, []);

  const handleSnooze = (days: number) => {
    if (snoozeAnchor) {
      void snooze(snoozeAnchor.item.id, days).catch(() => {
        showSnackbar('Could not snooze this item. Please try again.', { variant: 'error' });
      });
    }
    closeSnoozeMenu();
  };

  const closeDetail = useCallback(() => {
    setDetailTarget(null);
    refresh();
  }, [refresh]);

  const handleItemClick = useCallback(
    (item: ActionItem) => {
      if (!item.target) return;
      if (!isWorkQueueDialogTarget(item.target.type)) return;
      const target = { type: item.target.type, id: item.target.id };

      // Inside the dialog a second one would bury the queue the reader started from.
      if (hostedInDialog) {
        window.open(PAGE_URL[target.type](target.id), '_blank', 'noopener');
        return;
      }
      setDetailTarget(target);
    },
    [hostedInDialog],
  );

  // Rendered in every state below: completing a task in the dialog can clear the last item
  // from this list, and the dialog going with it would take its completion prompt too. The key keeps
  // it the same dialog when the list empties.
  const detailDialog = hostedInDialog ? null : (
    <WorkQueueDetailDialog key="detail" target={detailTarget} onClose={closeDetail} />
  );

  if (isLoading) {
    return (
      <>
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
        {detailDialog}
      </>
    );
  }

  if (items.length === 0) {
    return (
      <>
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {emptyMessage}
          </Typography>
        </Box>
        {detailDialog}
      </>
    );
  }

  return (
    <>
      {/* No summary band: the counts and value it totals are the record's own, and the
          tab strip already carries the count. */}
      <WorkQueueTable
        actionItems={items}
        isLoading={isLoading}
        isValidating={isValidating}
        onRefresh={refresh}
        onOpenItem={handleItemClick}
        onSnooze={openSnoozeMenu}
        height={tableHeight}
        enablePreview={false}
      />

      <Menu
        anchorEl={snoozeAnchor?.el ?? null}
        open={Boolean(snoozeAnchor)}
        onClose={closeSnoozeMenu}
      >
        {SNOOZE_OPTIONS.map((opt) => (
          <MenuItem key={opt.days} onClick={() => handleSnooze(opt.days)}>
            {opt.label}
          </MenuItem>
        ))}
      </Menu>

      {detailDialog}
    </>
  );
}
